(function () {
  let lastCustomerToken = null;
  let lastShopifyCustomerId = null;
  let smileSdkInitialized = false;
  let lastPublishableKey = null;
  let smileJsLoadPromise = null;
  let ensureSmileLoginPromise = null;

  const SMILE_JS_SCRIPT_URL = "https://sdk.smile.io/smile-js/v1/smile.js";
  const SMILE_JS_LOADER_ATTR = "data-smile-js-loader";

  const SMILE_SESSION_MAX_AGE_MS = 4 * 60 * 1000;

  let smileAuthMemory = {
    publishableKey: null,
    customerToken: null,
    shopifyCustomerId: null,
    fetchedAt: 0,
  };

  const SMILE_JS_PRELOAD_KEYS = [
    "pointsSettings",
    "earningRules",
    "pointsProducts",
    "customerPointsWallet",
    "customerPointsProducts",
    "rewardFulfillments",
    "customerVipStatus",
    "vipTiers",
  ];

  function getBackendBaseUrl() {
    return window.SUMMIT_CHECKOUT_CONFIG?.backendBaseUrl || null;
  }

  function resolveShopDomain() {
    return window.Shopify && window.Shopify.shop
      ? window.Shopify.shop
      : location.hostname;
  }

  function getSupabaseAccessToken() {
    return window.supabaseSession?.session?.access_token || null;
  }

  function isSmileTokenFresh() {
    return (
      !!smileAuthMemory.customerToken &&
      Date.now() - smileAuthMemory.fetchedAt < SMILE_SESSION_MAX_AGE_MS
    );
  }

  function buildPayloadFromMemory() {
    if (!smileAuthMemory.customerToken || !smileAuthMemory.publishableKey) {
      return null;
    }
    return {
      success: true,
      publishableKey: smileAuthMemory.publishableKey,
      customerToken: smileAuthMemory.customerToken,
      shopifyCustomerId: smileAuthMemory.shopifyCustomerId,
      usedCache: true,
    };
  }

  function isSmileAuthError(e) {
    if (!e) return false;
    const status = e.status ?? e.statusCode ?? e.response?.status;
    if (status === 401) return true;
    const msg = String(e.message || e.error?.message || e).toLowerCase();
    if (msg.includes("401")) return true;
    if (msg.includes("unauthorized")) return true;
    if (msg.includes("invalid bearer")) return true;
    if (msg.includes("invalid token")) return true;
    if (msg.includes("could not be identified")) return true;
    if (msg.includes("customer could not")) return true;
    return false;
  }

  function invalidateSmileSessionCache() {
    smileAuthMemory = {
      publishableKey: null,
      customerToken: null,
      shopifyCustomerId: null,
      fetchedAt: 0,
    };
    lastCustomerToken = null;
    lastShopifyCustomerId = null;
    window.__summit_smile_lastCustomerToken = null;
  }

  async function fetchSmileSessionPayload() {
    const backendBaseUrl = getBackendBaseUrl();
    if (!backendBaseUrl) return null;

    const accessToken = getSupabaseAccessToken();
    if (!accessToken) return null;

    const shop = resolveShopDomain();
    const resp = await fetch(`${backendBaseUrl}/api/smile/session`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ shop }),
      credentials: "include",
    });

    const payload = await resp.json().catch(() => null);
    if (!resp.ok || !payload?.success) {
      console.warn("[SmileBridge] Smile session HTTP failed", {
        httpStatus: resp.status,
        message: payload?.message || null,
      });
      return null;
    }

    smileAuthMemory = {
      publishableKey: payload.publishableKey,
      customerToken: payload.customerToken,
      shopifyCustomerId: payload.shopifyCustomerId || null,
      fetchedAt: Date.now(),
    };

    return { ...payload, usedCache: false };
  }

  function ensureSmileJsLoaded() {
    // If Smile is already available (loaded by app embed or us), use it
    if (window.Smile && typeof window.Smile.initialize === "function") {
      return Promise.resolve(window.Smile);
    }

    if (smileJsLoadPromise) return smileJsLoadPromise;

    smileJsLoadPromise = new Promise((resolve, reject) => {
      const done = () => {
        if (window.Smile && typeof window.Smile.initialize === "function") {
          resolve(window.Smile);
          return true;
        }
        return false;
      };

      document.addEventListener(
        "smile-js-loaded",
        () => { done(); },
        { once: true }
      );

      // Poll for Smile becoming available (app embed loads it asynchronously)
      var pollInterval = setInterval(() => {
        if (done()) { clearInterval(pollInterval); }
      }, 300);

      // If Smile app embed hasn't loaded the SDK after 5 seconds, inject our own
      setTimeout(() => {
        if (done()) return;
        if (!document.querySelector(`script[${SMILE_JS_LOADER_ATTR}]`)) {
          console.log("[SmileBridge] Smile app embed slow, loading smile.js directly");
          var script = document.createElement("script");
          script.async = true;
          script.src = SMILE_JS_SCRIPT_URL;
          script.setAttribute(SMILE_JS_LOADER_ATTR, "true");
          script.onerror = function () { clearInterval(pollInterval); reject(new Error("Failed to load Smile.js")); };
          document.head.appendChild(script);
        }
      }, 5000);

      // Final timeout
      setTimeout(() => {
        clearInterval(pollInterval);
        if (done()) return;
        reject(new Error("Timed out waiting for Smile.js"));
      }, 20000);
    });

    return smileJsLoadPromise;
  }

  async function initializeSmile({ publishableKey, customerToken }) {
    if (!publishableKey) {
      throw new Error("Missing Smile publishableKey");
    }

    const Smile = await ensureSmileJsLoaded();

    if (!customerToken) {
      lastCustomerToken = null;
      lastShopifyCustomerId = null;
      return Smile;
    }

    console.log("[Rewards] Smile runtime re-auth start", {
      hasPublishableKey: !!publishableKey,
      hasCustomerToken: !!customerToken,
    });

    // Try to login first (Smile app embed usually initializes the SDK).
    // If login fails because SDK isn't initialized, initialize ourselves.
    if (smileSdkInitialized) {
      try {
        await Smile.customer.login({ customerToken, preload: [] });
      } catch (loginErr) {
        var msg = String(loginErr?.message || "");
        if (msg.includes("already logged in")) {
          console.log("[SmileBridge] customer already active via Smile app, proceeding");
        } else {
          throw loginErr;
        }
      }
    } else {
      try {
        await Smile.customer.login({ customerToken, preload: [] });
      } catch (loginErr) {
        var loginMsg = String(loginErr?.message || "");
        if (loginMsg.includes("already logged in")) {
          console.log("[SmileBridge] customer already active, proceeding");
        } else {
          // SDK not initialized yet — do it ourselves
          try {
            await Smile.initialize({ publishableKey, customerToken, preload: [] });
          } catch (initErr) {
            if (!String(initErr?.message || "").includes("already initialized")) {
              throw initErr;
            }
            // Already initialized by app embed — try login again
            try { await Smile.customer.login({ customerToken, preload: [] }); } catch (_e) { /* ignore */ }
          }
        }
      }
    }

    smileSdkInitialized = true;
    lastPublishableKey = publishableKey;

    try {
      if (typeof Smile.preload === "function") {
        await Smile.preload(SMILE_JS_PRELOAD_KEYS);
      }
    } catch (preloadErr) {
      console.warn("[SmileBridge] Smile.preload failed (non-fatal)", preloadErr);
    }

    lastCustomerToken = customerToken;
    lastShopifyCustomerId = smileAuthMemory.shopifyCustomerId;
    window.__summit_smile_lastCustomerToken = customerToken;
    console.log("[Rewards] Smile runtime re-auth success");

    return Smile;
  }

  async function ensureFreshSmileLoginImpl(options = {}) {
    const forceRefresh = !!options.forceRefresh;

    const accessToken = getSupabaseAccessToken();
    if (!accessToken) {
      await clearSmileState();
      return null;
    }

    if (!forceRefresh && isSmileTokenFresh()) {
      const cachedPayload = buildPayloadFromMemory();
      if (cachedPayload) {
        console.log("[Rewards] using cached Smile session");
        await initializeSmile({
          publishableKey: cachedPayload.publishableKey,
          customerToken: cachedPayload.customerToken,
        });
        return cachedPayload;
      }
    }

    console.log("[Rewards] session refresh start");
    const payload = await fetchSmileSessionPayload();
    if (!payload) return null;
    console.log("[Rewards] session refresh success");

    await initializeSmile({
      publishableKey: payload.publishableKey,
      customerToken: payload.customerToken,
    });

    lastShopifyCustomerId = payload.shopifyCustomerId || null;
    return payload;
  }

  async function ensureFreshSmileLogin(options = {}) {
    if (ensureSmileLoginPromise) return ensureSmileLoginPromise;

    ensureSmileLoginPromise = (async () => {
      try {
        return await ensureFreshSmileLoginImpl(options);
      } finally {
        ensureSmileLoginPromise = null;
      }
    })();

    return ensureSmileLoginPromise;
  }

  async function syncAfterLogin() {
    return ensureFreshSmileLogin({ forceRefresh: true });
  }

  async function withSmileRetry(fn) {
    try {
      return await fn();
    } catch (e) {
      if (!isSmileAuthError(e)) throw e;
      console.warn("[Rewards] retry after auth failure", e);
      invalidateSmileSessionCache();
      await ensureFreshSmileLogin({ forceRefresh: true });
      return await fn();
    }
  }

  async function clearSmileState() {
    console.log("[SmileBridge] clearing Smile state (logout or unauthenticated)");
    invalidateSmileSessionCache();
    try {
      const Smile = await ensureSmileJsLoaded();
      if (smileSdkInitialized && typeof Smile.customer?.logout === "function") {
        Smile.customer.logout();
      }
    } catch (_e) {
      // SDK never loaded — nothing to clear
    } finally {
      smileSdkInitialized = false;
      lastPublishableKey = null;
    }
  }

  function openSmilePanel() {
    console.warn(
      "[SmileBridge] openSmilePanel is disabled (custom rewards UI; no Smile UI panel)"
    );
  }

  /**
   * Initialize Smile SDK with just the publishableKey (no customer auth).
   * Used for guest/public data: earningRules, pointsProducts, vipTiers.
   */
  async function initSmilePublic() {
    var Smile = await ensureSmileJsLoaded();

    // If we already know SDK is initialized, just return it
    if (smileSdkInitialized) return Smile;

    // Try to initialize (will fail harmlessly if app embed already did it)
    var pk = window.SUMMIT_CHECKOUT_CONFIG?.smilePublishableKey;
    if (pk) {
      try {
        await Smile.initialize({ publishableKey: pk, preload: [] });
        smileSdkInitialized = true;
      } catch (e) {
        if (String(e?.message || "").includes("already initialized")) {
          smileSdkInitialized = true;
        } else {
          console.warn("[SmileBridge] public init failed", e);
        }
      }
    }

    return Smile;
  }

  window.SummitSmileBridge = {
    syncAfterLogin,
    ensureFreshSmileLogin,
    fetchFreshSmileSession: fetchSmileSessionPayload,
    withSmileRetry,
    invalidateSmileSessionCache,
    isSmileTokenFresh,
    isSmileAuthError,
    initializeSmile,
    ensureSmileJsLoaded,
    initSmilePublic,
    openSmilePanel,
    clearSmileState,
  };

  window.addEventListener("supabase:session-change", (event) => {
    const isAuthenticated = !!event?.detail?.isAuthenticated;
    if (!isAuthenticated) {
      console.log("[Rewards] clearing Smile state on logout");
      clearSmileState();
    } else {
      console.log("[SmileBridge] session-change: user authenticated, syncing Smile");
      ensureFreshSmileLogin({ forceRefresh: false }).catch((err) => {
        console.warn("[SmileBridge] auto-sync on session-change failed:", err);
      });
    }
  });
})();
